// src/pages/LessonPage.js
import { useParams, useLocation, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/authContext';

const API_BASE = process.env.REACT_APP_API_URL;

export default function LessonPage() {
  const { lessonId, moduleId } = useParams();
  const location = useLocation();
  const { user } = useAuth();

  const [classroomId, setClassroomId] = useState(null);

  const [lesson, setLesson] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [saved, setSaved] = useState(false);

  const [studentInput, setStudentInput] = useState('');
  const [feedback, setFeedback] = useState('');
  const [loadingLesson, setLoadingLesson] = useState(true);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const qp = new URLSearchParams(location.search);
    const cid = qp.get('classroomId');
    if (cid) setClassroomId(cid);
  }, [location.search]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      if (!lessonId) return;
      setLoadingLesson(true);
      setError('');

      try {
        // 1) Lesson
        const { data: lessonData, error: lessonErr } = await supabase
          .from('lessons')
          .select('*')
          .eq('id', lessonId)
          .maybeSingle();
        if (lessonErr || !lessonData) throw lessonErr || new Error('Lesson not found');

        const normalizedQuiz = Array.isArray(lessonData.quiz)
          ? lessonData.quiz.map((q, idx) => ({
              ...q,
              id: q.id || `q${idx}`,
              options: Array.isArray(q.options) ? q.options : [],
            }))
          : [];
        const normalizedLesson = { ...lessonData, quiz: normalizedQuiz };
        if (!isMounted) return;
        setLesson(normalizedLesson);

        // 2) Progress
        if (!user) {
          setSubmitted(false);
          setSaved(false);
          setStudentInput('');
          setFeedback('');
          setAnswers({});
          return;
        }

        if (classroomId) {
          const { data: progress, error: progErr } = await supabase
            .from('classroom_lesson_progress')
            .select('completed, answers, writing_response, teacher_feedback, teacher_score, score')
            .eq('student_id', user.id)
            .eq('classroom_id', classroomId)
            .eq('lesson_id', lessonId)
            .maybeSingle();
          if (progErr) throw progErr;

          if (progress) {
            setSubmitted(!!progress.completed);
            setSaved(!!progress.completed);
            setStudentInput(progress.writing_response || '');
            setFeedback(progress.teacher_feedback || '');
            if (normalizedQuiz.length > 0 && progress.answers) {
              setAnswers(progress.answers || {});
            }
          } else {
            setSubmitted(false);
            setSaved(false);
            setStudentInput('');
            setFeedback('');
            setAnswers({});
          }
        } else if (moduleId) {
          const { data: progress, error: progErr } = await supabase
            .from('module_progress')
            .select('completed, quiz_answers, writing_response, feedback, score')
            .eq('user_id', user.id)
            .eq('module_id', moduleId)
            .eq('lesson_id', lessonId)
            .maybeSingle();
          if (progErr) throw progErr;

          if (progress) {
            setSubmitted(!!progress.completed);
            setSaved(!!progress.completed);
            setStudentInput(progress.writing_response || '');
            setFeedback(progress.feedback || '');
            if (normalizedQuiz.length > 0 && progress.quiz_answers) {
              setAnswers(progress.quiz_answers || {});
            }
          } else {
            setSubmitted(false);
            setSaved(false);
            setStudentInput('');
            setFeedback('');
            setAnswers({});
          }
        } else {
          setSubmitted(false);
          setSaved(false);
          setStudentInput('');
          setFeedback('');
          setAnswers({});
        }
      } catch (e) {
        console.error(e);
        if (isMounted) setError('Failed to load the lesson or progress.');
      } finally {
        if (isMounted) setLoadingLesson(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [lessonId, moduleId, classroomId, user]);

  const handleAnswer = (qId, opt) => {
    setAnswers((prev) => ({ ...prev, [qId]: opt }));
  };

  const score = useMemo(() => {
    if (!lesson?.quiz?.length) return 0;
    return lesson.quiz.reduce((sum, q) => sum + (answers[q.id] === q.correct ? 1 : 0), 0);
  }, [lesson, answers]);

  const saveProgress = async (extra = {}) => {
    if (!user || !lessonId) return false;
    try {
      if (classroomId) {
        const payload = {
          student_id: user.id,
          classroom_id: classroomId,
          lesson_id: lessonId,
          completed: true,
          answers: Object.keys(answers).length ? answers : null,
          score,
          writing_response: studentInput.trim() || null,
          ...extra,
        };
        const { error } = await supabase
          .from('classroom_lesson_progress')
          .upsert(payload, { onConflict: ['student_id', 'classroom_id', 'lesson_id'] });
        if (error) throw error;
      } else if (moduleId) {
        const payload = {
          user_id: user.id,
          module_id: moduleId,
          lesson_id: lessonId,
          completed: true,
          quiz_answers: Object.keys(answers).length ? answers : null,
          score,
          writing_response: studentInput.trim() || null,
          updated_at: new Date().toISOString(),
          ...extra,
        };
        const { error } = await supabase
          .from('module_progress')
          .upsert(payload, { onConflict: ['user_id', 'module_id', 'lesson_id'] });
        if (error) throw error;
      } else {
        return true;
      }
      return true;
    } catch (e) {
      console.error('Failed to save progress:', e?.message || e);
      return false;
    }
  };

  const handleFeedback = async () => {
    if (!studentInput.trim() || !lesson) return;
    setLoadingFeedback(true);
    try {
      const res = await fetch(`${API_BASE}/api/lesson-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: lesson.writing_prompt || lesson.writingPrompt || '',
          input: studentInput,
        }),
      });
      const data = await res.json();
      const fb = data.feedback || 'No feedback received.';
      setFeedback(fb);

      const success = await saveProgress(
        classroomId ? { teacher_feedback: fb } : { feedback: fb }
      );
      if (success) setSaved(true);
    } catch (err) {
      console.error(err);
      setFeedback('Error generating feedback.');
    } finally {
      setLoadingFeedback(false);
    }
  };

  const handleSubmitLesson = async () => {
    setSubmitted(true);
    const success = await saveProgress();
    if (success) setSaved(true);
  };

  // UI helpers
  const totalQuestions = lesson?.quiz?.length || 0;
  const answeredCount = useMemo(
    () =>
      totalQuestions
        ? lesson.quiz.reduce((n, q) => n + (answers[q.id] ? 1 : 0), 0)
        : 0,
    [lesson, answers, totalQuestions]
  );

  if (loadingLesson) {
    return (
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <HeaderSkeleton />
        <CardSkeleton />
        <CardSkeleton />
      </div>
    );
  }

  if (error || !lesson) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          {error || 'Lesson not found.'}
        </div>
      </div>
    );
  }

  const showScore = submitted || saved;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-8">
      {/* Top header with breadcrumbs and quick stats */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <nav className="text-sm text-gray-500">
            <ol className="list-reset inline-flex items-center gap-1">
              <li>
                <Link className="hover:underline" to={moduleId ? `/modules/view/${moduleId}` : '/modules'}>
                  {moduleId ? 'Module' : 'Modules'}
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-700">{lesson.title}</li>
            </ol>
          </nav>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">{lesson.title}</h1>
          {(moduleId || classroomId) && (
            <p className="mt-1 text-sm text-gray-600">
              {moduleId && <span>Module: {moduleId}</span>}
              {moduleId && classroomId && <span className="mx-2">•</span>}
              {classroomId && <span>Classroom: {classroomId}</span>}
            </p>
          )}
        </div>

        {/* Progress pill */}
        {totalQuestions > 0 && (
          <div className="rounded-lg border bg-white px-4 py-3 shadow-sm">
            <p className="text-sm text-gray-600">Quiz progress</p>
            <div className="mt-1 flex items-center gap-3">
              <div className="w-40 h-2 rounded bg-gray-100 overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all"
                  style={{ width: `${Math.round((answeredCount / totalQuestions) * 100)}%` }}
                />
              </div>
              <p className="text-sm font-medium text-gray-800">
                {answeredCount}/{totalQuestions} answered
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Reading */}
      {lesson.reading && (
        <Card title="Reading" subtitle="Review the lesson content">
          <div className="prose max-w-none text-gray-800 whitespace-pre-wrap">{lesson.reading}</div>
        </Card>
      )}

      {/* Quiz */}
      {Array.isArray(lesson.quiz) && lesson.quiz.length > 0 && (
        <Card
          title="Quiz"
          subtitle="Select the best answer for each question. Submit to see correctness."
          footer={
            <>
              {!showScore && (
                <button
                  onClick={() => setSubmitted(true)}
                  className="inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2 font-medium text-white hover:bg-green-500"
                >
                  Submit Answers
                </button>
              )}
              {showScore && (
                <div className="text-sm text-gray-700">
                  Score: <span className="font-semibold">{score} / {lesson.quiz.length}</span>
                </div>
              )}
            </>
          }
        >
          <div className="space-y-6">
            {lesson.quiz.map((q, idx) => {
              const correct = answers[q.id] === q.correct;
              const showResult = showScore;
              return (
                <div key={q.id} className="rounded-lg border border-gray-200 p-4 bg-white">
                  <p className="font-medium text-gray-900">Q{idx + 1}. {q.text}</p>
                  <div className="mt-3 grid gap-2">
                    {q.options.map((opt) => (
                      <label
                        key={opt}
                        className={`flex items-center gap-3 rounded-md border px-3 py-2 text-sm cursor-pointer ${
                          submitted || saved ? 'opacity-90' : 'hover:border-blue-400'
                        } ${
                          showResult && opt === q.correct ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white'
                        }`}
                      >
                        <input
                          type="radio"
                          name={q.id}
                          value={opt}
                          disabled={submitted || saved}
                          checked={answers[q.id] === opt}
                          onChange={() => handleAnswer(q.id, opt)}
                          className="accent-blue-600"
                        />
                        <span className="text-gray-800">{opt}</span>
                      </label>
                    ))}
                  </div>

                  {showResult && (
                    <p className={`mt-2 text-sm ${correct ? 'text-green-600' : 'text-red-600'}`}>
                      {correct ? 'Correct' : `Correct answer: ${q.correct}`}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Writing */}
      {(lesson.allow_writing || lesson.allowWriting) && (
        <Card
          title="Practice Writing"
          subtitle="Respond to the prompt and request AI feedback to improve"
          footer={
            <>
              {!saved && (
                <button
                  onClick={handleFeedback}
                  disabled={loadingFeedback}
                  className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                >
                  {loadingFeedback ? 'Evaluating...' : 'Get AI Feedback'}
                </button>
              )}
            </>
          }
        >
          {lesson.writing_prompt && (
            <p className="mb-2 text-gray-700 italic">{lesson.writing_prompt}</p>
          )}
          <textarea
            rows={6}
            value={studentInput}
            onChange={(e) => setStudentInput(e.target.value)}
            placeholder="Write your response here..."
            className="w-full rounded-md border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={saved}
          />

          {feedback && (
            <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4">
              <h4 className="font-semibold text-gray-900">AI Feedback</h4>
              <p className="mt-1 whitespace-pre-wrap text-gray-800">{feedback}</p>
            </div>
          )}
        </Card>
      )}

      {/* Final submit */}
      {(classroomId || moduleId) && !saved && (
        <div className="flex items-center justify-end border-t border-gray-200 pt-6">
          <button
            onClick={handleSubmitLesson}
            className="inline-flex items-center justify-center rounded-md bg-purple-600 px-5 py-2.5 font-medium text-white hover:bg-purple-500"
          >
            Submit Lesson
          </button>
        </div>
      )}
      {saved && (
        <div className="flex items-center justify-between border-t border-gray-200 pt-6">
          <div className="inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-green-800">
            <span>✅</span>
            <span className="font-medium">Lesson submitted</span>
          </div>

          <div className="text-sm text-gray-600">
            {moduleId && (
              <Link to={`/modules/view/${moduleId}`} className="text-blue-700 hover:underline">
                Back to module
              </Link>
            )}
            {classroomId && (
              <Link to={`/classrooms/student/${classroomId}`} className="text-blue-700 hover:underline">
                Back to classroom
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Small UI helpers ---------- */

function Card({ title, subtitle, footer, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        {subtitle && <p className="mt-1 text-sm text-gray-600">{subtitle}</p>}
      </div>
      <div className="px-5 py-5">{children}</div>
      {footer && (
        <div className="border-t border-gray-100 px-5 py-4 flex items-center justify-between">
          {footer}
        </div>
      )}
    </section>
  );
}

function HeaderSkeleton() {
  return (
    <div className="space-y-3">
      <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
      <div className="h-8 w-72 bg-gray-100 rounded animate-pulse" />
      <div className="h-4 w-56 bg-gray-100 rounded animate-pulse" />
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4">
        <div className="h-5 w-40 bg-gray-100 rounded animate-pulse" />
        <div className="mt-2 h-4 w-64 bg-gray-100 rounded animate-pulse" />
      </div>
      <div className="px-5 py-5 space-y-3">
        <div className="h-4 w-full bg-gray-100 rounded animate-pulse" />
        <div className="h-4 w-5/6 bg-gray-100 rounded animate-pulse" />
        <div className="h-4 w-2/3 bg-gray-100 rounded animate-pulse" />
        <div className="h-4 w-1/2 bg-gray-100 rounded animate-pulse" />
      </div>
    </div>
  );
}
